import { supabase } from '@/integrations/supabase/client';
import { Transaction, Category, CreditCard, BankAccount, Budget, Goal } from '@/types';
import { getCurrentActiveProfileId } from './supabase-profiles-operations';
import { encryptField, decryptField } from './encryption-service';
import { decryptBatch } from './batch-decrypt-service';
import { logger } from '@/utils/logger';
import type { EncryptedData } from '@/types/encryption';
// Helper function to get current profile ID
export const getCurrentProfileId = (): string | null => {
  const profileId = getCurrentActiveProfileId();
  logger.debug('[Profile] Current active profile ID:', profileId);
  return profileId;
};

// Helper function to add profile_id to data
export const addProfileId = <T extends Record<string, any>>(data: T): T & { profile_id: string | null } => {
  return {
    ...data,
    profile_id: getCurrentProfileId()
  };
};

// TRANSACTIONS
// Helper function to map frontend status to database status
const mapStatusToDb = (status?: string): string => {
  switch (status) {
    case 'paid':
      return 'efetivada';
    case 'pending':
      return 'pendente';
    case 'partial':
      return 'parcial';
    default:
      return 'efetivada';
  }
};

// Helper function to map database status to frontend status
const mapStatusFromDb = (dbStatus?: string): 'paid' | 'pending' | 'partial' => {
  switch (dbStatus) {
    case 'pendente':
      return 'pending';
    case 'parcial':
      return 'partial';
    case 'efetivada':
      return 'paid';
    case 'cancelada':
      return 'pending'; // treat cancelada as pending for now
    default:
      return 'paid';
  }
};

// Helper to normalize UUID values for database
const normalizeUUIDForDB = (value: any): string | null => {
  if (!value || value === '' || value === 'undefined') return null;
  return value;
};

export const saveTransactionToSupabase = async (transaction: Transaction): Promise<void> => {
  logger.debug('[Supabase] Saving transaction with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const descriptionEncrypted = await encryptField(transaction.description);
    const amountEncrypted = await encryptField(String(transaction.amount));
    
    const transactionData = addProfileId({
      id: transaction.id,
      description: '[ENCRYPTED]',
      description_encrypted: descriptionEncrypted.encrypted,
      description_nonce: descriptionEncrypted.nonce,
      amount: 0, // Security: real value only in encrypted field
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      category_id: normalizeUUIDForDB(transaction.categoryId),
      date: transaction.date,
      type: transaction.type,
      account_id: normalizeUUIDForDB(transaction.accountId),
      payment_method: transaction.paymentMethod,
      card_id: normalizeUUIDForDB(transaction.cardId),
      bill_card_id: normalizeUUIDForDB(transaction.billCardId),
      status: mapStatusToDb(transaction.status),
      installments: transaction.installments || 1,
      installment_number: transaction.installmentNumber || 1,
      parent_transaction_id: normalizeUUIDForDB(transaction.parentTransactionId),
      release_id: normalizeUUIDForDB(transaction.releaseId),
      is_bill_payment: transaction.isBillPayment || false,
      original_amount: transaction.originalAmount || null,
      paid_portion: transaction.paidPortion || 0,
      bill_period: transaction.billPeriod || null,
      bill_due_date: transaction.billDueDate || null, // NOVO: persistir billDueDate
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    logger.debug('[Supabase] Normalized transaction data for DB (encrypted)');

    const { error } = await supabase
      .from('transactions')
      .upsert(transactionData);

    if (error) {
      console.error('[Supabase] Error saving transaction:', error);
      
      // Provide user-friendly error messages for common issues
      if (error.code === '23503' && error.message.includes('category_id')) {
        throw new Error('Categoria não encontrada. Por favor, selecione outra categoria e tente novamente.');
      }
      
      throw new Error(`Erro ao salvar transação: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da transação');
  }
};

// Helper to chunk array into batches
const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

// Batch sizes for performance
const DECRYPT_BATCH_SIZE = 10;
const ENCRYPT_BATCH_SIZE = 10;
const UPSERT_BATCH_SIZE = 50;

/**
 * Salvar múltiplas transações em lote para melhor performance
 * - Criptografa em paralelo com limite
 * - Faz upsert em lotes
 */
export const saveTransactionsBatchToSupabase = async (transactions: Transaction[]): Promise<void> => {
  if (transactions.length === 0) return;
  
  logger.info(`[Supabase] Saving ${transactions.length} transactions in batch...`);
  const startTime = performance.now();
  
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) {
    throw new Error('Usuário não autenticado');
  }
  
  // Função para preparar uma transação (criptografar)
  const prepareTransaction = async (transaction: Transaction) => {
    const [descriptionEncrypted, amountEncrypted] = await Promise.all([
      encryptField(transaction.description),
      encryptField(String(transaction.amount))
    ]);
    
    return addProfileId({
      id: transaction.id,
      description: '[ENCRYPTED]',
      description_encrypted: descriptionEncrypted.encrypted,
      description_nonce: descriptionEncrypted.nonce,
      amount: 0,
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      category_id: normalizeUUIDForDB(transaction.categoryId),
      date: transaction.date,
      type: transaction.type,
      account_id: normalizeUUIDForDB(transaction.accountId),
      payment_method: transaction.paymentMethod,
      card_id: normalizeUUIDForDB(transaction.cardId),
      bill_card_id: normalizeUUIDForDB(transaction.billCardId),
      status: mapStatusToDb(transaction.status),
      installments: transaction.installments || 1,
      installment_number: transaction.installmentNumber || 1,
      parent_transaction_id: normalizeUUIDForDB(transaction.parentTransactionId),
      release_id: normalizeUUIDForDB(transaction.releaseId),
      is_bill_payment: transaction.isBillPayment || false,
      original_amount: transaction.originalAmount || null,
      paid_portion: transaction.paidPortion || 0,
      bill_period: transaction.billPeriod || null,
      bill_due_date: transaction.billDueDate || null, // NOVO: persistir billDueDate
      user_id: userId
    });
  };
  
  // Criptografar em paralelo com limite de concorrência
  const preparedTransactions: any[] = [];
  const encryptBatches = chunkArray(transactions, ENCRYPT_BATCH_SIZE);
  
  for (const batch of encryptBatches) {
    const batchResults = await Promise.all(batch.map(prepareTransaction));
    preparedTransactions.push(...batchResults);
  }
  
  logger.debug(`[Supabase] Encrypted ${preparedTransactions.length} transactions`);
  
  // Fazer upsert em lotes
  const upsertBatches = chunkArray(preparedTransactions, UPSERT_BATCH_SIZE);
  
  for (const batch of upsertBatches) {
    const { error } = await supabase
      .from('transactions')
      .upsert(batch);
    
    if (error) {
      console.error('[Supabase] Error in batch upsert:', error);
      throw new Error(`Erro ao salvar transações em lote: ${error.message}`);
    }
  }
  
  const endTime = performance.now();
  logger.info(`[Supabase] ✅ Batch save completed in ${(endTime - startTime).toFixed(0)}ms`);
};

/**
 * Salvar múltiplas transações em lote COM RETRY automático
 * - Tenta até maxRetries vezes com backoff exponencial
 * - Ideal para operações críticas como pagamento de fatura
 */
export const saveTransactionsBatchToSupabaseWithRetry = async (
  transactions: Transaction[], 
  maxRetries: number = 3
): Promise<void> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.debug(`[Supabase] Batch save attempt ${attempt}/${maxRetries}...`);
      await saveTransactionsBatchToSupabase(transactions);
      return; // Sucesso!
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.error(`[Supabase] Attempt ${attempt} failed:`, lastError.message);
      
      if (attempt < maxRetries) {
        // Backoff exponencial: 1s, 2s, 4s...
        const delay = Math.pow(2, attempt - 1) * 1000;
        logger.debug(`[Supabase] Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // Todas as tentativas falharam
  throw new Error(`Falha ao salvar transações após ${maxRetries} tentativas: ${lastError?.message}`);
};

export const loadTransactionsFromSupabase = async (): Promise<Transaction[]> => {
  const profileId = getCurrentProfileId();
  logger.debug('[Supabase] Loading transactions for profile:', profileId);
  
  let query = supabase
    .from('transactions')
    .select('*')
    .order('date', { ascending: false });

  // Filter by profile if one is active
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading transactions:', error);
    throw new Error(`Erro ao carregar transações: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  logger.debug(`[Supabase] Batch decrypting ${data.length} transactions...`);
  const startTime = performance.now();

  // Helper function to clean encrypted placeholders
  const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
    if (decrypted) return decrypted;
    if (!fallback || fallback === '[ENCRYPTED]') return '';
    return fallback;
  };

  // Collect all encrypted fields for batch decryption
  const allEncryptedItems: EncryptedData[] = [];
  
  for (const row of data) {
    // Description
    allEncryptedItems.push({
      encrypted: row.description_encrypted || '',
      nonce: row.description_nonce || ''
    });
    // Amount
    allEncryptedItems.push({
      encrypted: row.amount_encrypted || '',
      nonce: row.amount_nonce || ''
    });
  }

  // Single batch decryption call
  let decryptedValues: string[] = [];
  try {
    decryptedValues = await decryptBatch(allEncryptedItems);
  } catch (batchError) {
    logger.error('[Supabase] Batch decryption failed, falling back to individual decryption:', batchError);
    // Fall back to individual decryption on batch failure
    decryptedValues = await Promise.all(
      allEncryptedItems.map(item => 
        decryptField(item).catch(() => '')
      )
    );
  }

  // Map decrypted values back to transactions
  const results: Transaction[] = [];
  const failedTransactions: string[] = [];

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const descIndex = i * 2;
    const amountIndex = i * 2 + 1;
    
    try {
      const description = decryptedValues[descIndex] || '';
      const amount = decryptedValues[amountIndex] || '';
      
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) && amount !== '') {
        logger.warn(`[Decrypt] Transaction ${row.id}: amount parsing failed: "${amount}"`);
      }
      
      const cleanDescription = cleanValue(description, row.description);
      const finalAmount = !isNaN(parsedAmount) ? parsedAmount : (row.amount ?? 0);
      
      // For card transactions, ensure originalAmount is filled
      let originalAmount = row.original_amount;
      if (row.card_id && row.type === 'despesa' && !row.is_bill_payment) {
        if (!originalAmount || originalAmount <= 0) {
          originalAmount = Math.abs(finalAmount);
        }
      }
      
      results.push({
        id: row.id,
        description: cleanDescription,
        amount: finalAmount,
        category: '',
        categoryId: row.category_id || '',
        date: row.date,
        type: row.type as 'receita' | 'despesa',
        account: '',
        accountId: row.account_id,
        paymentMethod: row.payment_method as any,
        cardId: row.card_id,
        billCardId: row.bill_card_id,
        status: mapStatusFromDb(row.status),
        installmentNumber: row.installment_number,
        installments: row.installments,
        parentTransactionId: row.parent_transaction_id,
        createdAt: row.created_at,
        releaseId: row.release_id,
        isBillPayment: row.is_bill_payment || false,
        originalAmount: originalAmount || undefined,
        paidPortion: row.paid_portion || 0,
        billPeriod: row.bill_period || undefined,
        billDueDate: row.bill_due_date || undefined,
      });
    } catch (processError) {
      failedTransactions.push(row.id);
      console.error(`[Decrypt] Transaction ${row.id}: processing failed`, processError);
      
      results.push({
        id: row.id,
        description: cleanValue('', row.description) || '⚠️ Erro ao descriptografar',
        amount: 0,
        category: '',
        categoryId: row.category_id || '',
        date: row.date,
        type: row.type as 'receita' | 'despesa',
        account: '',
        accountId: row.account_id,
        paymentMethod: row.payment_method as any,
        cardId: row.card_id,
        billCardId: row.bill_card_id,
        status: mapStatusFromDb(row.status),
        installmentNumber: row.installment_number,
        installments: row.installments,
        parentTransactionId: row.parent_transaction_id,
        createdAt: row.created_at,
        hasDecryptError: true,
      });
    }
  }

  const endTime = performance.now();
  logger.info(`[Supabase] ✅ Batch decrypted ${results.length} transactions in ${(endTime - startTime).toFixed(0)}ms`);

  if (failedTransactions.length > 0) {
    logger.warn(`[Supabase] ⚠️ ${failedTransactions.length} de ${data.length} transações com erro de descriptografia`);
  }

  return results;
};

export const deleteTransactionFromSupabase = async (transactionId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting transaction:', transactionId);

  // First, delete any child transactions that reference this one as parent
  // This is a safety measure in addition to the CASCADE DELETE constraint
  const { data: childTransactions, error: childError } = await supabase
    .from('transactions')
    .select('id')
    .eq('parent_transaction_id', transactionId);

  if (childError) {
    console.error('[Supabase] Error finding child transactions:', childError);
  }

  if (childTransactions && childTransactions.length > 0) {
    logger.debug(`[Supabase] Found ${childTransactions.length} child transactions to delete first`);
    
    // Delete children first (in case CASCADE isn't working or not yet applied)
    const { error: deleteChildrenError } = await supabase
      .from('transactions')
      .delete()
      .eq('parent_transaction_id', transactionId);

    if (deleteChildrenError) {
      console.error('[Supabase] Error deleting child transactions:', deleteChildrenError);
      throw new Error(`Erro ao excluir transações filhas: ${deleteChildrenError.message}`);
    }
  }

  // Now delete the parent transaction
  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('id', transactionId);

  if (error) {
    console.error('[Supabase] Error deleting transaction:', error);
    throw new Error(`Erro ao excluir transação: ${error.message}`);
  }
};

// CATEGORIES
export const saveCategoryToSupabase = async (category: Category): Promise<void> => {
  logger.debug('[Supabase] Saving category with profile:', getCurrentProfileId());
  
  const categoryData = addProfileId({
    id: category.id,
    name: category.name, // PLAINTEXT - no encryption
    type: category.type,
    color: category.color,
    icon: category.icon,
    is_default: category.isDefault || false,
    user_id: (await supabase.auth.getUser()).data.user?.id
  });

  const { error } = await supabase
    .from('categories')
    .upsert(categoryData);

  if (error) {
    // Friendly duplicate error
    if (error.code === '23505') {
      throw new Error('Já existe uma categoria com esse nome. Escolha outro nome.');
    }
    console.error('[Supabase] Error saving category:', error);
    throw new Error(`Erro ao salvar categoria: ${error.message}`);
  }
};

export const loadCategoriesFromSupabase = async (): Promise<Category[]> => {
  logger.debug('[Supabase] Loading categories for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('categories')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, filter by NULL profile_id
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading categories:', error);
    throw new Error(`Erro ao carregar categorias: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Direct mapping - no decryption needed (plaintext names)
  return data.map(row => ({
    id: row.id,
    name: row.name, // PLAINTEXT directly
    type: row.type as 'receita' | 'despesa',
    color: row.color,
    icon: row.icon,
    isDefault: row.is_default
  }));
};

export const deleteCategoryFromSupabase = async (categoryId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting category:', categoryId);

  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', categoryId);

  if (error) {
    console.error('[Supabase] Error deleting category:', error);
    throw new Error(`Erro ao excluir categoria: ${error.message}`);
  }
};

// CARDS
export const saveCardToSupabase = async (card: CreditCard): Promise<void> => {
  logger.debug('[Supabase] Saving card with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(card.name);
    const bankEncrypted = await encryptField(card.bank || '');
    const limitEncrypted = await encryptField(String(card.limit));
    const usedEncrypted = await encryptField(String(card.used));
    
    const cardData = addProfileId({
      id: card.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      bank: '[ENCRYPTED]',
      bank_encrypted: bankEncrypted.encrypted,
      bank_nonce: bankEncrypted.nonce,
      type: 'credito', // Fixed: Use 'credito' to match database constraint
      limit: 0, // Security: real value only in encrypted field
      limit_encrypted: limitEncrypted.encrypted,
      limit_nonce: limitEncrypted.nonce,
      used: 0, // Security: real value only in encrypted field
      used_encrypted: usedEncrypted.encrypted,
      used_nonce: usedEncrypted.nonce,
      due_date: card.dueDate,
      closing_date: card.closingDate,
      color: card.color,
      account_id: card.accountId,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('cards')
      .upsert(cardData);

    if (error) {
      console.error('[Supabase] Error saving card:', error);
      throw new Error(`Erro ao salvar cartão: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do cartão');
  }
};

export const loadCardsFromSupabase = async (): Promise<CreditCard[]> => {
  logger.debug('[Supabase] Loading cards for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('cards')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading cards:', error);
    throw new Error(`Erro ao carregar cartões: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  const startTime = performance.now();

  const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
    if (decrypted) return decrypted;
    if (!fallback || fallback === '[ENCRYPTED]') return '';
    return fallback;
  };

  // Collect all encrypted fields (4 per card: name, bank, limit, used)
  const allEncryptedItems: EncryptedData[] = [];
  for (const row of data) {
    allEncryptedItems.push({ encrypted: row.name_encrypted || '', nonce: row.name_nonce || '' });
    allEncryptedItems.push({ encrypted: row.bank_encrypted || '', nonce: row.bank_nonce || '' });
    allEncryptedItems.push({ encrypted: row.limit_encrypted || '', nonce: row.limit_nonce || '' });
    allEncryptedItems.push({ encrypted: row.used_encrypted || '', nonce: row.used_nonce || '' });
  }

  // Single batch decryption call
  let decryptedValues: string[] = [];
  try {
    decryptedValues = await decryptBatch(allEncryptedItems);
  } catch (batchError) {
    logger.error('[Supabase] Cards batch decryption failed:', batchError);
    decryptedValues = await Promise.all(
      allEncryptedItems.map(item => decryptField(item).catch(() => ''))
    );
  }

  // Map results
  const results: CreditCard[] = data.map((row, index) => {
    const baseIndex = index * 4;
    const name = decryptedValues[baseIndex] || '';
    const bank = decryptedValues[baseIndex + 1] || '';
    const limit = decryptedValues[baseIndex + 2] || '';
    const used = decryptedValues[baseIndex + 3] || '';
    
    const parsedLimit = parseFloat(limit);
    const parsedUsed = parseFloat(used);
    
    return {
      id: row.id,
      name: cleanValue(name, row.name),
      bank: cleanValue(bank, row.bank),
      limit: !isNaN(parsedLimit) ? parsedLimit : 0,
      used: !isNaN(parsedUsed) ? parsedUsed : 0,
      dueDate: row.due_date,
      closingDate: row.closing_date,
      color: row.color,
      accountId: row.account_id
    };
  });

  const endTime = performance.now();
  logger.info(`[Supabase] ✅ Batch decrypted ${results.length} cards in ${(endTime - startTime).toFixed(0)}ms`);
  
  return results;
};

export const deleteCardFromSupabase = async (cardId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting card:', cardId);

  const { error } = await supabase
    .from('cards')
    .delete()
    .eq('id', cardId);

  if (error) {
    console.error('[Supabase] Error deleting card:', error);
    throw new Error(`Erro ao excluir cartão: ${error.message}`);
  }
};

// ACCOUNTS
export const saveAccountToSupabase = async (account: BankAccount): Promise<void> => {
  logger.debug('[Supabase] Saving account with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(account.name);
    const bankEncrypted = await encryptField(account.bank || '');
    const balanceEncrypted = await encryptField(String(account.balance));
    const initialBalanceEncrypted = await encryptField(String(account.initialBalance || 0));
    const accountNumberEncrypted = await encryptField(account.accountNumber || '');
    
    const accountData = addProfileId({
      id: account.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      bank: '[ENCRYPTED]',
      bank_encrypted: bankEncrypted.encrypted,
      bank_nonce: bankEncrypted.nonce,
      type: account.type || account.accountType || 'corrente',
      account_type: account.accountType || account.type || 'corrente',
      balance: 0, // Security: real value only in encrypted field
      balance_encrypted: balanceEncrypted.encrypted,
      balance_nonce: balanceEncrypted.nonce,
      initial_balance: 0, // Security: real value only in encrypted field
      initial_balance_encrypted: initialBalanceEncrypted.encrypted,
      initial_balance_nonce: initialBalanceEncrypted.nonce,
      account_number: '[ENCRYPTED]',
      account_number_encrypted: accountNumberEncrypted.encrypted,
      account_number_nonce: accountNumberEncrypted.nonce,
      color: account.color,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('accounts')
      .upsert(accountData);

    if (error) {
      console.error('[Supabase] Error saving account:', error);
      throw new Error(`Erro ao salvar conta: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Account save error:', error);
    // Propagate the real error message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Erro ao salvar conta');
  }
};

export const loadAccountsFromSupabase = async (): Promise<BankAccount[]> => {
  logger.debug('[Supabase] Loading accounts for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('accounts')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading accounts:', error);
    throw new Error(`Erro ao carregar contas: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  const startTime = performance.now();

  const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
    if (decrypted) return decrypted;
    if (!fallback || fallback === '[ENCRYPTED]') return '';
    return fallback;
  };

  // Collect all encrypted fields (5 per account: name, bank, balance, initialBalance, accountNumber)
  const allEncryptedItems: EncryptedData[] = [];
  for (const row of data) {
    allEncryptedItems.push({ encrypted: row.name_encrypted || '', nonce: row.name_nonce || '' });
    allEncryptedItems.push({ encrypted: row.bank_encrypted || '', nonce: row.bank_nonce || '' });
    allEncryptedItems.push({ encrypted: row.balance_encrypted || '', nonce: row.balance_nonce || '' });
    allEncryptedItems.push({ encrypted: row.initial_balance_encrypted || '', nonce: row.initial_balance_nonce || '' });
    allEncryptedItems.push({ encrypted: row.account_number_encrypted || '', nonce: row.account_number_nonce || '' });
  }

  // Single batch decryption call
  let decryptedValues: string[] = [];
  try {
    decryptedValues = await decryptBatch(allEncryptedItems);
  } catch (batchError) {
    logger.error('[Supabase] Accounts batch decryption failed:', batchError);
    decryptedValues = await Promise.all(
      allEncryptedItems.map(item => decryptField(item).catch(() => ''))
    );
  }

  // Map results
  const results: BankAccount[] = data.map((row, index) => {
    const baseIndex = index * 5;
    const name = decryptedValues[baseIndex] || '';
    const bank = decryptedValues[baseIndex + 1] || '';
    const balance = decryptedValues[baseIndex + 2] || '';
    const initialBalance = decryptedValues[baseIndex + 3] || '';
    const accountNumber = decryptedValues[baseIndex + 4] || '';
    
    const parsedBalance = parseFloat(balance);
    const parsedInitialBalance = parseFloat(initialBalance);
    
    return {
      id: row.id,
      name: cleanValue(name, row.name),
      bank: cleanValue(bank, row.bank),
      type: row.type,
      accountType: row.account_type as 'corrente' | 'poupanca' | 'investimento',
      balance: !isNaN(parsedBalance) ? parsedBalance : (row.balance ?? 0),
      initialBalance: !isNaN(parsedInitialBalance) ? parsedInitialBalance : (row.initial_balance ?? 0),
      accountNumber: cleanValue(accountNumber, row.account_number),
      color: row.color
    };
  });

  const endTime = performance.now();
  logger.info(`[Supabase] ✅ Batch decrypted ${results.length} accounts in ${(endTime - startTime).toFixed(0)}ms`);
  
  return results;
};

export const deleteAccountFromSupabase = async (accountId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting account:', accountId);

  const { error } = await supabase
    .from('accounts')
    .delete()
    .eq('id', accountId);

  if (error) {
    console.error('[Supabase] Error deleting account:', error);
    throw new Error(`Erro ao excluir conta: ${error.message}`);
  }
};

// BUDGETS
export const saveBudgetToSupabase = async (budget: Budget): Promise<void> => {
  logger.debug('[Supabase] Saving budget with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(budget.name);
    const amountEncrypted = await encryptField(String(budget.amount));
    const spentEncrypted = await encryptField(String(budget.spent || 0));
    
    const budgetData = addProfileId({
      id: budget.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      amount: 0, // Security: real value only in encrypted field
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      spent: 0, // Security: real value only in encrypted field
      spent_encrypted: spentEncrypted.encrypted,
      spent_nonce: spentEncrypted.nonce,
      category_id: budget.categoryId,
      period: budget.period,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('budgets')
      .upsert(budgetData);

    if (error) {
      console.error('[Supabase] Error saving budget:', error);
      throw new Error(`Erro ao salvar orçamento: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do orçamento');
  }
};

export const loadBudgetsFromSupabase = async (): Promise<Budget[]> => {
  logger.debug('[Supabase] Loading budgets for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('budgets')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading budgets:', error);
    throw new Error(`Erro ao carregar orçamentos: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  const startTime = performance.now();

  const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
    if (decrypted) return decrypted;
    if (!fallback || fallback === '[ENCRYPTED]') return '';
    return fallback;
  };

  // Collect all encrypted fields (3 per budget: name, amount, spent)
  const allEncryptedItems: EncryptedData[] = [];
  for (const row of data) {
    allEncryptedItems.push({ encrypted: row.name_encrypted || '', nonce: row.name_nonce || '' });
    allEncryptedItems.push({ encrypted: row.amount_encrypted || '', nonce: row.amount_nonce || '' });
    allEncryptedItems.push({ encrypted: row.spent_encrypted || '', nonce: row.spent_nonce || '' });
  }

  // Single batch decryption call
  let decryptedValues: string[] = [];
  try {
    decryptedValues = await decryptBatch(allEncryptedItems);
  } catch (batchError) {
    logger.error('[Supabase] Budgets batch decryption failed:', batchError);
    decryptedValues = await Promise.all(
      allEncryptedItems.map(item => decryptField(item).catch(() => ''))
    );
  }

  // Map results
  const results: Budget[] = data.map((row, index) => {
    const baseIndex = index * 3;
    const name = decryptedValues[baseIndex] || '';
    const amount = decryptedValues[baseIndex + 1] || '';
    const spent = decryptedValues[baseIndex + 2] || '';
    
    const parsedAmount = parseFloat(amount);
    const parsedSpent = parseFloat(spent);
    
    return {
      id: row.id,
      name: cleanValue(name, row.name),
      amount: !isNaN(parsedAmount) ? parsedAmount : 0,
      spent: !isNaN(parsedSpent) ? parsedSpent : 0,
      categoryId: row.category_id,
      period: row.period as 'diario' | 'semanal' | 'mensal' | 'anual',
      createdAt: row.created_at || new Date().toISOString()
    };
  });

  const endTime = performance.now();
  logger.info(`[Supabase] ✅ Batch decrypted ${results.length} budgets in ${(endTime - startTime).toFixed(0)}ms`);
  
  return results;
};

export const deleteBudgetFromSupabase = async (budgetId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting budget:', budgetId);

  const { error } = await supabase
    .from('budgets')
    .delete()
    .eq('id', budgetId);

  if (error) {
    console.error('[Supabase] Error deleting budget:', error);
    throw new Error(`Erro ao excluir orçamento: ${error.message}`);
  }
};

// GOALS
export const saveGoalToSupabase = async (goal: Goal): Promise<void> => {
  logger.debug('[Supabase] Saving goal with profile:', getCurrentProfileId());
  
  try {
    // Encrypt sensitive fields - use Goal's fields: amount, currentValue, targetValue
    const nameEncrypted = await encryptField(goal.name);
    const targetAmountEncrypted = await encryptField(String(goal.targetValue || goal.amount || 0));
    const currentAmountEncrypted = await encryptField(String(goal.currentValue || goal.saved || 0));
    
    const goalData = addProfileId({
      id: goal.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      target_amount: 0, // Security: real value only in encrypted field
      target_amount_encrypted: targetAmountEncrypted.encrypted,
      target_amount_nonce: targetAmountEncrypted.nonce,
      current_amount: 0, // Security: real value only in encrypted field
      current_amount_encrypted: currentAmountEncrypted.encrypted,
      current_amount_nonce: currentAmountEncrypted.nonce,
      deadline: goal.deadline,
      category: goal.category,
      user_id: (await supabase.auth.getUser()).data.user?.id
    });

    const { error } = await supabase
      .from('goals')
      .upsert(goalData);

    if (error) {
      console.error('[Supabase] Error saving goal:', error);
      throw new Error(`Erro ao salvar meta: ${error.message}`);
    }
  } catch (error) {
    console.error('[Supabase] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da meta');
  }
};

export const loadGoalsFromSupabase = async (): Promise<Goal[]> => {
  logger.debug('[Supabase] Loading goals for profile:', getCurrentProfileId());
  
  let query = supabase
    .from('goals')
    .select('*')
    .order('name', { ascending: true });

  // Filter by profile if one is active
  const profileId = getCurrentProfileId();
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase] Error loading goals:', error);
    throw new Error(`Erro ao carregar metas: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  const startTime = performance.now();

  const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
    if (decrypted) return decrypted;
    if (!fallback || fallback === '[ENCRYPTED]') return '';
    return fallback;
  };

  // Collect all encrypted fields (3 per goal: name, targetAmount, currentAmount)
  const allEncryptedItems: EncryptedData[] = [];
  for (const row of data) {
    allEncryptedItems.push({ encrypted: row.name_encrypted || '', nonce: row.name_nonce || '' });
    allEncryptedItems.push({ encrypted: row.target_amount_encrypted || '', nonce: row.target_amount_nonce || '' });
    allEncryptedItems.push({ encrypted: row.current_amount_encrypted || '', nonce: row.current_amount_nonce || '' });
  }

  // Single batch decryption call
  let decryptedValues: string[] = [];
  try {
    decryptedValues = await decryptBatch(allEncryptedItems);
  } catch (batchError) {
    logger.error('[Supabase] Goals batch decryption failed, falling back to individual:', batchError);
    decryptedValues = await Promise.all(
      allEncryptedItems.map(item => decryptField(item).catch(() => ''))
    );
  }

  // Map results back to goals
  const results: Goal[] = data.map((row, index) => {
    const baseIndex = index * 3;
    const name = decryptedValues[baseIndex] || '';
    const targetAmount = decryptedValues[baseIndex + 1] || '';
    const currentAmount = decryptedValues[baseIndex + 2] || '';
    
    const parsedTargetAmount = parseFloat(targetAmount);
    const parsedCurrentAmount = parseFloat(currentAmount);
    
    return {
      id: row.id,
      name: cleanValue(name, row.name),
      amount: !isNaN(parsedTargetAmount) ? parsedTargetAmount : 0,
      targetValue: !isNaN(parsedTargetAmount) ? parsedTargetAmount : 0,
      currentValue: !isNaN(parsedCurrentAmount) ? parsedCurrentAmount : 0,
      saved: !isNaN(parsedCurrentAmount) ? parsedCurrentAmount : 0,
      deadline: row.deadline || '',
      categoryId: row.category,
      category: row.category,
      createdAt: row.created_at || new Date().toISOString()
    };
  });

  const endTime = performance.now();
  logger.info(`[Supabase] Batch decrypted ${results.length} goals in ${(endTime - startTime).toFixed(0)}ms`);
  
  return results;
};

export const deleteGoalFromSupabase = async (goalId: string): Promise<void> => {
  logger.debug('[Supabase] Deleting goal:', goalId);

  const { error } = await supabase
    .from('goals')
    .delete()
    .eq('id', goalId);

  if (error) {
    console.error('[Supabase] Error deleting goal:', error);
    throw new Error(`Erro ao excluir meta: ${error.message}`);
  }
};

/**
 * Get or create the "Outros" category for a given transaction type
 * This queries the user's actual categories in the database
 */
export async function getOrCreateOthersCategory(type: 'receita' | 'despesa'): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.error('[getOrCreateOthersCategory] No authenticated user');
    return null;
  }

  const profileId = getCurrentProfileId();

  // First, try to find existing "Outros" category for this type
  let query = supabase
    .from('categories')
    .select('id')
    .eq('user_id', user.id)
    .eq('type', type)
    .ilike('name', 'outros');

  if (profileId) {
    query = query.eq('profile_id', profileId);
  }

  const { data: existingCategories, error: findError } = await query.limit(1);

  if (findError) {
    console.error('[getOrCreateOthersCategory] Error finding category:', findError);
    return null;
  }

  if (existingCategories && existingCategories.length > 0) {
    console.log('[getOrCreateOthersCategory] Found existing category:', existingCategories[0].id);
    return existingCategories[0].id;
  }

  // Category doesn't exist, create it
  console.log('[getOrCreateOthersCategory] Creating new "Outros" category for type:', type);
  
  const newCategory = {
    user_id: user.id,
    profile_id: profileId,
    name: 'Outros',
    type: type,
    color: type === 'receita' ? '#10B981' : '#6B7280',
    icon: 'MoreHorizontal',
    is_default: true
  };

  const { data: createdCategory, error: createError } = await supabase
    .from('categories')
    .insert(newCategory)
    .select('id')
    .single();

  if (createError) {
    console.error('[getOrCreateOthersCategory] Error creating category:', createError);
    return null;
  }

  console.log('[getOrCreateOthersCategory] Created new category:', createdCategory.id);
  return createdCategory.id;
}

// AUTH CHECK
export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data } = await supabase.auth.getUser();
  return !!data.user;
};
